import type { ReactElement } from 'react';

import type { HelpDiagram } from '@/features/help/diagrams';

/**
 * 操作マニュアル用の模式図（D-246 / D-254）。
 * 管理画面のカードと同じ枠・色だけを使い、新しい図形コンポーネントは増やさない。
 */
export function HelpDiagrams({ diagrams }: { diagrams: HelpDiagram[] }): ReactElement | null {
  if (diagrams.length === 0) return null;
  return (
    <>
      {diagrams.map((diagram) => (
        <section
          key={diagram.title}
          className="rounded-sm border border-wpborder bg-white px-4 py-4"
        >
          <h2 className="mb-2 font-semibold text-wptext-strong">{diagram.title}</h2>
          {diagram.lead && <p className="mb-3 text-wptext-muted">{diagram.lead}</p>}
          {diagram.kind === 'flow' && <FlowDiagram steps={diagram.steps} />}
          {diagram.kind === 'map' && <ImpactMap rows={diagram.rows} />}
          {diagram.kind === 'example' && (
            <>
              <VisualSteps
                rows={diagram.rows.map((row) => ({ title: row.label, body: row.value }))}
              />
              {diagram.after && (
                <p className="mt-3 text-wptext-muted">
                  <span className="font-semibold text-wptext-strong">この後・注意: </span>
                  {diagram.after}
                </p>
              )}
            </>
          )}
        </section>
      ))}
    </>
  );
}

/** 番号付きステップカード（手順・具体例で共用） */
export function VisualSteps({
  rows,
}: {
  rows: { title?: string; body: string }[];
}): ReactElement {
  return (
    <ol className="flex flex-col gap-2">
      {rows.map((row, index) => (
        <li key={`${index}-${row.title ?? row.body}`} className="flex gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-wpaccent text-sm font-semibold text-white"
          >
            {index + 1}
          </span>
          <div className="min-w-0 flex-1 rounded-sm border border-wpborder bg-wpcanvas/40 px-3 py-2">
            {row.title && (
              <p className="mb-1 font-semibold text-wptext-strong">{row.title}</p>
            )}
            <p className="text-wptext-strong">{row.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function FlowDiagram({
  steps,
}: {
  steps: { title: string; body: string }[];
}): ReactElement {
  return (
    <ol className="flex flex-col gap-2 lg:flex-row lg:items-stretch">
      {steps.map((step, index) => (
        <li
          key={step.title}
          className="flex w-full flex-col gap-2 lg:min-w-0 lg:flex-1 lg:flex-row lg:items-stretch"
        >
          <div className="flex min-w-0 flex-1 flex-col rounded-sm border-2 border-wpaccent bg-wpcanvas/60 px-3 py-3">
            <span className="mb-2 flex items-center gap-2 text-xs font-semibold text-wpaccent">
              <span
                aria-hidden="true"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-wpaccent text-[11px] font-semibold text-white"
              >
                {index + 1}
              </span>
              {step.title}
            </span>
            <span className="text-wptext-strong">{step.body}</span>
          </div>
          {index < steps.length - 1 && (
            <>
              <span
                aria-hidden="true"
                className="self-center text-lg font-semibold text-wpaccent lg:hidden"
              >
                ↓
              </span>
              <span
                aria-hidden="true"
                className="hidden shrink-0 self-center text-lg font-semibold text-wpaccent lg:inline"
              >
                →
              </span>
            </>
          )}
        </li>
      ))}
    </ol>
  );
}

function ImpactMap({ rows }: { rows: { master: string; appearsIn: string }[] }): ReactElement {
  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <li
          key={row.master}
          className="flex flex-col gap-2 rounded-sm border border-wpborder bg-wpcanvas/40 p-3 sm:flex-row sm:items-stretch"
        >
          <div className="shrink-0 rounded-sm border-2 border-wpaccent bg-white px-3 py-2 sm:w-[11rem]">
            <p className="text-xs font-semibold text-wpaccent">登録する側</p>
            <p className="font-semibold text-wptext-strong">{row.master}</p>
          </div>
          <span
            aria-hidden="true"
            className="self-center text-lg font-semibold text-wpaccent sm:px-1"
          >
            <span className="sm:hidden">↓</span>
            <span className="hidden sm:inline">→</span>
          </span>
          <div className="min-w-0 flex-1 rounded-sm border border-wpborder bg-white px-3 py-2">
            <p className="text-xs font-semibold text-wptext-muted">効く場所（画面）</p>
            <p className="text-wptext-strong">{row.appearsIn}</p>
          </div>
        </li>
      ))}
    </ul>
  );
}
